import { fetchUser } from './api_users.js';
import moment, { months } from 'moment-timezone';
import { ws_scaleCalibration, ws_resetScaleCalibration} from './api_wsFunctions.js'
import PlantDetailsModal from '../components/Pages/Plants/PlantDetailsModal.js';

const API_URL = process.env.REACT_APP_API_URL;

function getTimezoneByIndex(index) {
    const timezones = [
        'America/Los_Angeles', // UTC -8
        'America/Denver',      // UTC -7
        'America/Chicago',     // UTC -6
        'America/New_York',    // UTC -5
        'America/Toronto',     // UTC -5
        'America/Sao_Paulo',   // UTC -3
        'Europe/London',       // UTC +0
        'Europe/Madrid',       // UTC +1
        'Europe/Paris',        // UTC +1
        'Europe/Rome',         // UTC +1
        'Europe/Berlin',       // UTC +1
        'Africa/Johannesburg', // UTC +2
        'Europe/Istanbul',     // UTC +3
        'Europe/Moscow',       // UTC +3
        'Asia/Dubai',          // UTC +4
        'Asia/Shanghai',       // UTC +8
        'Asia/Hong_Kong',      // UTC +8
        'Asia/Singapore',      // UTC +8
        'Asia/Tokyo',          // UTC +9
        'Australia/Sydney'     // UTC +10
    ];

    return timezones[index] || 'UTC'; // Retorna 'UTC' por defecto si el índice no es válido
}

// Helper function to handle the response from fetch calls
async function handleResponse(response) {
    if (!response.ok) {
        const errorData = await response.json();
        return { error: true, message: errorData.message || 'Network error' };
    }
    return { error: false, data: await response.json() };
}

// Generic fetch function
async function fetchData(url, options = {}) {
    try {
        const response = await fetch(url, options);
        return await handleResponse(response);
    } catch (error) {
        console.error('Fetch error:', error);
        return { error: true, message: error.message };
    }
}

// Update an existing hub
export async function updateHubName(hubID, hub_name, token) {
    if (!hubID || !hub_name) {
        console.error('Invalid input for updating hub');
        return { error: true, message: 'Both hubID and hub data are required' };
    }
    const options = {
        method: "PUT",
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(hub_name),
    };
    return fetchData(`${API_URL}/hubs/${encodeURIComponent(hubID)}`, options);
}

export async function unassignHub(hubID, token) {
    if (!hubID) {
        console.error('No hubID provided');
        return { error: true, message: 'hubID is required' };
    }

    const options = {
        method: "POST",
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    return fetchData(`${API_URL}/hubs/${encodeURIComponent(hubID)}/unassign`, options);
}


// Fetch pots for a specific hub
export async function fetchPotsForHub(hubID, token) {
    if (!hubID) {
        console.error('No hubID provided');
        return { error: true, message: 'hubID is required' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/hubs/${encodeURIComponent(hubID)}/pots`, options);
}

// Fetch measures for a hub by date
export async function fetchMeasuresForHubByDate(hubID, startDate, endDate, token) {
    if (!hubID) {
        console.error('No hubID provided');
        return { error: true, message: 'hubID is required' };
    }

    if (!startDate || !endDate) {
        console.error('Start date and end date are required');
        return { error: true, message: 'Start date and end date must be provided' };
    }

    const user = await fetchUser();
    const timezone = getTimezoneByIndex(user.data.timezone);

    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const formattedStartDate = new Date(startDate).toISOString();
    const formattedEndDate = new Date(endDate).toISOString();

    const response = await fetchData(`${API_URL}/hubs/${encodeURIComponent(hubID)}/measuresbyDate/?start_date=${encodeURIComponent(formattedStartDate)}&end_date=${encodeURIComponent(formattedEndDate)}`, options);

    if (response.error) {
        console.error('Failed to fetch measures:', response.message);
        return response;
    }

    // Convert UTC times in response data to user's timezone
    const measuresWithLocalTime = response.data.map(measure => {
        const utcDate = moment.utc(measure.timestamp).tz(timezone);
        return { ...measure, timestamp: utcDate.format('YYYY-MM-DD HH:mm:ssZ') };
    });

    return { error: false, data: measuresWithLocalTime };
}

// Fetch the last measure for a hub
export async function fetchLastMeasureForHub(hubID, token) {
    if (!hubID) {
        console.error('No hubID provided');
        return { error: true, message: 'hubID is required' };
    }

    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    const user = await fetchUser();
    const timezone = getTimezoneByIndex(user.data.timezone);

    const response = await fetchData(`${API_URL}/hubs/${encodeURIComponent(hubID)}/lastMeasure`, options);

    if (response.error) {
        console.error('Failed to fetch the last measure:', response.message);
        return response;
    }

    if (response.data && response.data.timestamp) {
        // Convert UTC time in response data to user's timezone
        const utcDate = moment.utc(response.data.timestamp).tz(timezone);
        return { error: false, data: { ...response.data, timestamp: utcDate.format('YYYY-MM-DD HH:mm:ssZ') } };
    }

    return response;
}

// Link hub to a user
export async function linkHubToUser(SN, TOKEN, token) {
    if (!SN || !TOKEN) {
        console.error('Missing data for linking hub to user');
        return { error: true, message: 'SN and TOKEN are required' };
    }
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ SN, TOKEN })
    };
    return fetchData(`${API_URL}/hubs/link`, options);
}

// Add hub to a space
export async function addHubToSpace(hub_id, space_id, token) {
    if (!hub_id || !space_id) {
        console.error('Missing data for adding hub to space');
        return { error: true, message: 'hubID and spaceID are required' };
    }
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ hub_id, space_id })
    };
    return fetchData(`${API_URL}/hubs/addToSpace`, options);
}

export async function calibrate_scale_defect(hub, token) {
    try {
        const lastMeasure = await fetchLastMeasureForHub(hub.id, token);
        console.log(lastMeasure);
        if (!lastMeasure.data || !hub.SN) {
            console.error('Missing data: lastMeasure or hub SN is undefined.');
            return;
        }
        ws_scaleCalibration(hub.SN, lastMeasure.data.water_level);
    } catch (error) {
        console.error('Failed to calibrate scale:', error);
    }
}

export async function calibrate_scale_defect_factory(hub, token) {
    try {
        const lastMeasure = await fetchLastMeasureForHub(hub.id, token);
        console.log(lastMeasure);
        if (!lastMeasure.data || !hub.SN) {
            console.error('Missing data: lastMeasure or hub SN is undefined.');
            return;
        }
        ws_resetScaleCalibration(hub.SN);
    } catch (error) {
        console.error('Failed to calibrate scale:', error);
    }
}

export async function estimatedBatteryLife(sendFrequency, measureFrequency, batteryCapacityMah = 1000) {
    // Estimated battery capacity in mAh (default: 1000 for standard batteries)
    const ESTIMATED_BATTERY_CAPACITY = batteryCapacityMah;

    // Raw consumption and time data
    const SEND_CURRENT_mA = 112.55;
    const SEND_TIME_s = 2.098;

    const MEASURE_CURRENT_mA = 34.77;
    const MEASURE_TIME_s = 0.5018;

    const POWER_UP_CURRENT_mA = 33.98;
    const POWER_UP_TIME_s = 0.2968;

    const PASSIVE_CURRENT_mA = 0.00082; // This is already in mA for direct use

    // Function to convert current (mA) and time (s) to mAh
    function to_mAh(current_mA, time_s) {
        return (current_mA * time_s) / 3600;
    }

    // Calculate consumption in mAh
    const SEND_CONSUMPTION_mAh = to_mAh(SEND_CURRENT_mA, SEND_TIME_s);
    const MEASURE_CONSUMPTION_mAh = to_mAh(MEASURE_CURRENT_mA, MEASURE_TIME_s);
    const POWER_UP_CONSUMPTION_mAh = to_mAh(POWER_UP_CURRENT_mA, POWER_UP_TIME_s);
    const PASSIVE_CONSUMPTION_mAh = PASSIVE_CURRENT_mA; // Already in mAh per hour

    /**
     * Calculate the total partial hourly consumption:
     * (2 / sendFrequency) * (
     *   SEND_CONSUMPTION_mAh +
     *   (sendFrequency / measureFrequency) * MEASURE_CONSUMPTION_mAh +
     *   (sendFrequency - (sendFrequency / measureFrequency)) * POWER_UP_CONSUMPTION_mAh
     * )
     *
     * - sendFrequency: Number of send events per hour
     * - measureFrequency: Number of measure events per hour
     *
     * This formula estimates the total energy consumed per hour,
     * considering sending, measuring, idle, and passive periods.
     */
    const totalHourConsumptionPartial =
        (2 / sendFrequency) * (
            SEND_CONSUMPTION_mAh +
            (sendFrequency / measureFrequency) * MEASURE_CONSUMPTION_mAh +
            (sendFrequency - (sendFrequency / measureFrequency)) * POWER_UP_CONSUMPTION_mAh
        );

    // Add the passive consumption to the partial total
    const totalHourConsumption = totalHourConsumptionPartial + PASSIVE_CONSUMPTION_mAh;

    /**
     * Calculate how many months the battery will last:
     * - totalHourConsumption: mAh consumed per hour
     * - 24 * 30: hours in a month (assuming 30 days per month)
     * - ESTIMATED_BATTERY_CAPACITY: total battery in mAh
     */
    const monthsRemaining = ESTIMATED_BATTERY_CAPACITY / (totalHourConsumption * 24 * 30);

    const monthsRemaining_reducted = monthsRemaining*0.845;

    // Return the estimated battery life in months
    return monthsRemaining_reducted;
}